
import { Suppliers } from "@/api";
import { Supplier } from "@/api/suppliers";
import PageTitle from "@/components/common/PageTitle.vue";
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";

const Auth = namespace("auth");

const emptySupplier = (): Supplier => ({
  name: "",
  hansaCode: "",
  vatNumber: "",
  contact: ""
});

@Component({ components: { PageTitle } })
export default class SuppliersPage extends Vue {
  @Auth.Getter("getUser") getUser!: any;

  pageHeaders = [{ icon: "mdi-truck-outline", header: "Suppliers" }];
  headers = [
    { text: "Supplier Name", value: "name", align: "start" },
    { text: "Hansa Code", value: "hansaCode", align: "start" },
    { text: "VAT Number", value: "vatNumber", align: "start" },
    { text: "Contact", value: "contact", align: "start" },
    {
      text: "Actions",
      value: "actions",
      align: "start",
      sortable: false,
      width: "150px"
    }
  ];

  suppliers: Supplier[] = [];
  loading = false;
  saving = false;
  showDialog = false;
  editingId: number | null = null;
  form: Supplier = emptySupplier();
  errors: Record<string, string[]> = {};
  filters: { search: string | null } = { search: null };

  private searchTimer: number | null = null;

  get permissions(): string[] {
    return (this.getUser && this.getUser.permissions) || [];
  }
  get canView(): boolean {
    return this.permissions.includes("view_supplier");
  }
  get canCreate(): boolean {
    return this.permissions.includes("create_supplier");
  }
  get canEdit(): boolean {
    return this.permissions.includes("edit_supplier");
  }
  get canDelete(): boolean {
    return this.permissions.includes("delete_supplier");
  }

  async created(): Promise<void> {
    if (!this.canView) return;
    await this.loadSuppliers();
  }

  // Typing in the search box should not fire a request per keystroke.
  @Watch("filters.search")
  onSearchChange(): void {
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => this.loadSuppliers(), 350);
  }

  async loadSuppliers(): Promise<void> {
    this.loading = true;
    try {
      this.suppliers = await Suppliers.list({
        search: this.filters.search || null
      });
    } catch (e) {
      await this.$error(e);
    } finally {
      this.loading = false;
    }
  }

  openCreate(): void {
    this.editingId = null;
    this.form = emptySupplier();
    this.errors = {};
    this.showDialog = true;
  }

  openEdit(supplier: Supplier): void {
    this.editingId = supplier.id || null;
    this.form = { ...supplier };
    this.errors = {};
    this.showDialog = true;
  }

  closeDialog(): void {
    this.showDialog = false;
    this.errors = {};
  }

  async save(): Promise<void> {
    this.saving = true;
    this.errors = {};
    try {
      if (this.editingId) {
        await Suppliers.update(this.editingId, this.form);
      } else {
        await Suppliers.create(this.form);
      }
      this.showDialog = false;
      await this.loadSuppliers();
      await this.$success("<strong>Success!</strong> Supplier saved.");
    } catch (e) {
      this.errors = this.fieldErrors(e);
      if (!Object.keys(this.errors).length) await this.$error(e);
    } finally {
      this.saving = false;
    }
  }

  async confirmDelete(supplier: Supplier): Promise<void> {
    const answer = await this.$dialog.error({
      text: `Delete "${supplier.name}"?`,
      title: "Warning",
      persistent: false,
      actions: { No: { text: "No" }, Yes: { color: "red", text: "Yes I do" } }
    });
    if (answer !== "Yes" || !supplier.id) return;
    try {
      await Suppliers.remove(supplier.id);
      await this.loadSuppliers();
      await this.$success("<strong>Success!</strong> Supplier deleted.");
    } catch (e) {
      await this.$error(e);
    }
  }

  // Laravel returns 422 with an errors bag keyed by the snake_case field.
  fieldErrors(error: any): Record<string, string[]> {
    const bag = error?.response?.data?.errors || error?.errors;
    if (!bag) return {};
    const toCamelKey = (key: string): string =>
      key.replace(/_([a-z])/g, (_match, letter) => letter.toUpperCase());

    return Object.keys(bag).reduce((carry: Record<string, string[]>, key) => {
      carry[toCamelKey(key)] = bag[key];
      return carry;
    }, {});
  }
}
